// @flow

import { all, put, takeEvery } from "redux-saga/effects";
import http from "@http";
import { call } from "redux-saga/effects";
import { downloadCsvFile } from "@sagas/utils";
import actionCreator from "@modules/reporting/actionCreator";
import * as actions from "@modules/reporting/constants/actions";
import type { Action } from "redux-action";
import config from "@config";

export function* onGetWorkForceHealth({ payload }: Object): Generator<void, void, *> {
  try {
    if (!payload || !payload.type) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    let response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/health/workforce?type=${payload.type}`,
    );

    yield put(actionCreator.GetWorkForceHealthSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetWorkForceHealthError(e.response));
  }
}

function* onExportWorkForceReport({
  payload,
}: Action<{ clientId: number, type: string }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.type) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/health/workforce/export?type=${payload.type}`,
    );

    downloadCsvFile(response.data, `Work Force Report-${payload.type}.csv`);
    yield put(actionCreator.ExportWorkForceReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.ExportWorkForceReportError(e.response));
  }
}

export function* onGetCarePlansReport({ payload }: Object): Generator<void, void, *> {
  try {
    if (!payload || !payload.type || !payload.dateRange) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    // BE does not expect params in snakecase (which we auto apply), so we have to do it like this
    const customDates =
      payload.fromDate && payload.toDate
        ? `&fromDate=${payload.fromDate}&toDate=${payload.toDate}`
        : "";

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/health/care-plan?type=${payload.type}&dateRange=${payload.dateRange}${customDates}`,
    );

    yield put(actionCreator.GetCarePlansReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetCarePlansReportError(e.response));
  }
}

function* onExportCarePlansReport({
  payload,
}: Action<{
  clientId: number,
  type: string,
  dateRange: string,
  fromDate?: string,
  toDate?: string,
}>): Generator<*, void, *> {
  try {
    if (!payload || !payload.type || !payload.dateRange) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const customDates =
      payload.fromDate && payload.toDate
        ? `&fromDate=${payload.fromDate}&toDate=${payload.toDate}`
        : "";

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/health/care-plan/export?type=${payload.type}&dateRange=${payload.dateRange}${customDates}`,
    );

    downloadCsvFile(response.data, `Care Plans Report-${payload.type}-${payload.dateRange}.csv`);
    yield put(actionCreator.ExportCarePlansReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.ExportCarePlansReportError(e.response));
  }
}

export function* onGetWellbeingAdviceReport({ payload }: Object): Generator<void, void, *> {
  try {
    if (!payload || !payload.type || !payload.dateRange) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const customDates =
      payload.fromDate && payload.toDate
        ? `&fromDate=${payload.fromDate}&toDate=${payload.toDate}`
        : "";

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/health/wellbeing-advice?type=${payload.type}&dateRange=${payload.dateRange}${customDates}`,
    );

    yield put(actionCreator.GetWellbeingAdviceReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetWellbeingAdviceReportError(e.response));
  }
}

function* onExportWellbeingAdviceReport({
  payload,
}: Action<{
  clientId: number,
  type: string,
  dateRange: string,
  fromDate?: string,
  toDate?: string,
}>): Generator<*, void, *> {
  try {
    if (!payload || !payload.type || !payload.dateRange) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const customDates =
      payload.fromDate && payload.toDate
        ? `&fromDate=${payload.fromDate}&toDate=${payload.toDate}`
        : "";

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/health/wellbeing-advice/export?type=${payload.type}&dateRange=${payload.dateRange}${customDates}`,
    );

    downloadCsvFile(
      response.data,
      `Wellbeing Advice Report-${payload.type}-${payload.dateRange}.csv`,
    );
    yield put(actionCreator.ExportWellbeingAdviceReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.ExportWellbeingAdviceReportError(e.response));
  }
}

export function* onGetNurseAssessmentsReport({ payload }: Object): Generator<void, void, *> {
  try {
    if (!payload || !payload.type || !payload.dateRange) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const page = (payload || {}).page || 1;
    const customDates =
      payload.fromDate && payload.toDate
        ? `&fromDate=${payload.fromDate}&toDate=${payload.toDate}`
        : "";

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/health/nurse-assessments?type=${payload.type}&dateRange=${payload.dateRange}${customDates}`,
      {
        params: {
          page,
          limit: config.results50PerPage,
        },
      },
    );

    yield put(actionCreator.GetNurseAssessmentsReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetNurseAssessmentsReportError(e.response));
  }
}

function* onExportNurseAssessmentsReport({
  payload,
}: Action<{
  clientId: number,
  type: string,
  dateRange: string,
  fromDate?: string,
  toDate?: string,
}>): Generator<*, void, *> {
  try {
    if (!payload || !payload.type || !payload.dateRange) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const customDates =
      payload.fromDate && payload.toDate
        ? `&fromDate=${payload.fromDate}&toDate=${payload.toDate}`
        : "";

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/health/nurse-assessments/export?type=${payload.type}&dateRange=${payload.dateRange}${customDates}`,
    );

    downloadCsvFile(
      response.data,
      `Nurse Assessments Report-${payload.type}-${payload.dateRange}.csv`,
    );
    yield put(actionCreator.ExportNurseAssessmentsReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.ExportNurseAssessmentsReportError(e.response));
  }
}

export function* HealthReportSaga(): Generator<*, void, void> {
  yield all([takeEvery(actions.GetWorkForceHealth, onGetWorkForceHealth)]);
  yield all([takeEvery(actions.ExportWorkForceReport, onExportWorkForceReport)]);

  yield all([takeEvery(actions.GetCarePlansReport, onGetCarePlansReport)]);
  yield all([takeEvery(actions.ExportCarePlansReport, onExportCarePlansReport)]);

  yield all([takeEvery(actions.GetWellbeingAdviceReport, onGetWellbeingAdviceReport)]);
  yield all([takeEvery(actions.ExportWellbeingAdviceReport, onExportWellbeingAdviceReport)]);

  yield all([takeEvery(actions.GetNurseAssessmentsReport, onGetNurseAssessmentsReport)]);
  yield all([takeEvery(actions.ExportNurseAssessmentsReport, onExportNurseAssessmentsReport)]);
}

export default HealthReportSaga;
